// app/components/navbar.tsx
"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import images from "../constants/images";
import strings from "../constants/strings";
import LoginPopup from "../login/login";
import { useAuth } from "../login/login";
import { Dancing_Script } from "next/font/google";

const dancingScript = Dancing_Script({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  display: "swap",
});

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();
  const [showPopup, setShowPopup] = useState(false);
  const { user } = useAuth();
  const [mounted, setMounted] = useState(false);

  // Store current page path for redirect after login/logout
  useEffect(() => {
    if (typeof window !== "undefined" && pathname) {
      // Don't store callback or auth pages as currentPagePath
      if (
        !pathname.includes("/auth/callback") &&
        !pathname.includes("/login")
      ) {
        sessionStorage.setItem("currentPagePath", pathname);
      }
    }
  }, [pathname]);

  useEffect(() => {
    // Set mounted to true after component mounts to prevent hydration mismatch
    setMounted(true);

    const handleResize = () => {
      // Close menu when switching to desktop
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // useEffect(() => {
  //   const logAccessToken = async () => {
  //     try {
  //       const session = await fetchAuthSession();
  //       const accessToken = session.tokens?.accessToken?.toString();
  //       if (accessToken) {
  //         console.log("🔐 Access Token:", accessToken);
  //       } else {
  //         console.log("⚠️ No access token found");
  //       }
  //     } catch (err) {
  //       console.error("❌ Failed to fetch access token:", err);
  //     }
  //   };

  //   logAccessToken();
  // }, []);
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close menu when clicking on a link (mobile)
  const handleLinkClick = () => {
    if (mounted) {
      setIsMenuOpen(false);
    }
  };

  if (
    pathname.includes("/design") ||
    pathname.includes("/customize") ||
    pathname.includes("/weddingwebsite") ||
    pathname.includes("/invite") ||
    pathname.includes("/demos")
  ) {
    return null;
  }
  // if (pathname.includes("slashslash")) {
  //   return null;
  // }
  const getBgClass = () => {
    if (pathname.startsWith("/blog") && !pathname.startsWith("/blog/"))
      return "bg-[#FFF6F8]";
    return "bg-[#fff] shadow-sm ";
  };
  return (
    <>
      <div className="w-full bg-[#FE406C] text-white text-base leading-6 font-normal tracking-normal sticky top-0 z-50">
        <div className="overflow-hidden">
          <div className="mwi-marquee py-2">
            <span className="mwi-marquee-text">
              Wedding Invites, Now at Special Prices
            </span>
            <span className="mwi-marquee-text">
              Wedding Invites, Now at Special Prices
            </span>
          </div>
        </div>
        <style jsx>{`
          @keyframes mwi-marquee-left {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          .mwi-marquee {
            display: inline-flex;
            white-space: nowrap;
            animation: mwi-marquee-left 18s linear infinite;
            will-change: transform;
            width: max-content;
          }
          .mwi-marquee-text {
            display: inline-block;
            white-space: nowrap;
            flex-shrink: 0;
            padding-left: 60px;
            padding-right: 60px;
          }
          @media (min-width: 768px) {
            .mwi-marquee-text {
              padding-left: 80px;
              padding-right: 80px;
            }
          }
        `}</style>
      </div>
      <header
        className={`sticky top-[40px] z-40 border-b md:py-4 h-[50px] md:h-[81px] ${getBgClass()} transition-all duration-300 ${
          mounted && isMenuOpen ? "md:blur-none blur-sm" : ""
        }`}
        style={{ borderColor: "rgba(0,0,0,0.14)" }}
      >
        <div className="w-full max-w-[1920px] mx-auto px-5 md:px-8 h-full flex items-center justify-between">
          {/* Desktop Logo */}
          <div className="hidden md:block">
            <Link href="/" className="flex items-center no-underline">
              <span
                className={`${dancingScript.className} text-[#FE406C] text-[34px] leading-[100%] font-normal tracking-normal`}
              >
                My Weddingly Invites
              </span>
            </Link>
          </div>

          {/* Mobile Header */}
          <div className="flex md:hidden items-center justify-between w-full">
            <Link
              href="/"
              className="flex items-center no-underline"
              onClick={handleLinkClick}
            >
              <span
                className={`${dancingScript.className} text-[#FE406C] text-[20px] leading-[100%] font-normal tracking-normal`}
              >
                My Weddingly Invites
              </span>
            </Link>

            <button
              onClick={toggleMenu}
              className="bg-transparent border-none text-gray-700 text-2xl cursor-pointer p-1 hover:text-[#FE406C] transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? "✕" : "☰"}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex flex-grow">
            <ul className="flex list-none m-0 p-0 justify-center">
              <li className="mx-[1.5rem] relative">
                <Link
                  href="/"
                  className={`text-[18px] font-[400] no-underline hover:text-[#FE406C] transition-colors ${
                    pathname === "/" ? "text-[#FE406C]" : "text-[#0B214A]"
                  }`}
                >
                  Home
                </Link>
              </li>
              <li className="mx-[1.5rem] relative">
                <Link
                  href="/wedding-invitations"
                  className={`text-[18px] font-[400] no-underline hover:text-[#FE406C] transition-colors flex items-center ${
                    pathname === "/wedding-invitations"
                      ? "text-[#FE406C]"
                      : "text-[#0B214A]"
                  }`}
                >
                  Wedding Cards
                </Link>
              </li>
              <li className="mx-[1.5rem] relative">
                <Link
                  href="/blog"
                  className={`text-[18px] font-[400] no-underline hover:text-[#FE406C] transition-colors flex items-center ${
                    pathname === "/blog" ? "text-[#FE406C]" : "text-[#0B214A]"
                  }`}
                >
                  Blogs
                </Link>
              </li>
              <li className="mx-[1.5rem] relative">
                <Link
                  href="/contact-us"
                  className={`text-[18px] font-[400] no-underline hover:text-[#FE406C] transition-colors flex items-center ${
                    pathname === "/contact-us"
                      ? "text-[#FE406C]"
                      : "text-[#0B214A]"
                  }`}
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </nav>
          {user ? (
            <div
              onClick={() => setShowPopup(true)}
              className="hidden md:block border border-[#F55080] text-[#FE406C] px-8 py-2 rounded-full text-[18px] font-[500] text-base hover:bg-[#FE406C] hover:text-white transition-all cursor-pointer"
            >
              Hi, {user?.attributes?.firstName || "User"}
            </div>
          ) : (
            <div
              onClick={() => setShowPopup(true)}
              className="hidden md:block border border-[#F55080] text-[#FE406C] px-8 py-2 rounded-full text-[18px] font-[500] text-base hover:bg-[#FE406C] hover:text-white transition-all cursor-pointer"
            >
              Login/Signup
            </div>
          )}
          {showPopup && <LoginPopup onClose={() => setShowPopup(false)} />}
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mounted && isMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 backdrop-blur-sm z-40 md:hidden"
            onClick={toggleMenu}
          ></div>

          {/* Side Menu - opens from right */}
          <nav className="fixed top-0 right-0 h-full w-80 bg-white z-50 shadow-lg transform transition-transform duration-300 ease-in-out md:hidden">
            <div className="flex flex-col h-full">
              {/* Menu Header */}
              <div className="flex justify-end items-center px-6 py-4">
                <button
                  onClick={toggleMenu}
                  className="text-gray-700 text-xl hover:text-[#FE406C] transition-colors p-1"
                  aria-label="Close menu"
                >
                  ✕
                </button>
              </div>

              {/* Menu Items */}
              <ul className="flex flex-col list-none m-0 p-0 flex-grow">
                <li>
                  {/* Current page highlighting commented out - was: pathname === "/" ? "text-[#FE406C] bg-red-50" : "text-[#0B214A] hover:text-[#FE406C]" */}
                  <Link
                    href="/"
                    className="block px-6 py-4 text-[18px] font-[400] no-underline hover:bg-gray-50 transition-colors text-[#0B214A] hover:text-[#FE406C]"
                    onClick={handleLinkClick}
                  >
                    Home
                  </Link>
                </li>
                <li>
                  {/* Current page highlighting commented out - was: pathname === "/wedding-invitations" ? "text-[#FE406C] bg-red-50" : "text-[#0B214A] hover:text-[#FE406C]" */}
                  <Link
                    href="/wedding-invitations"
                    className="block px-6 py-4 text-[18px] font-[400] no-underline hover:bg-gray-50 transition-colors text-[#0B214A] hover:text-[#FE406C]"
                    onClick={handleLinkClick}
                  >
                    Wedding Cards
                  </Link>
                </li>
                <li>
                  {/* Current page highlighting commented out - was: pathname === "/contact-us" ? "text-[#FE406C] bg-red-50" : "text-[#0B214A] hover:text-[#FE406C]" */}
                  <Link
                    href="/blog"
                    className="block px-6 py-4 text-[18px] font-[400] no-underline hover:bg-gray-50 transition-colors text-[#0B214A] hover:text-[#FE406C]"
                    onClick={handleLinkClick}
                  >
                    Blogs
                  </Link>
                </li>
                <li>
                  {/* Current page highlighting commented out - was: pathname === "/blog" ? "text-[#FE406C] bg-red-50" : "text-[#0B214A] hover:text-[#FE406C]" */}
                  <Link
                    href="/contact-us"
                    className="block px-6 py-4 text-[18px] font-[400] no-underline hover:bg-gray-50 transition-colors text-[#0B214A] hover:text-[#FE406C]"
                    onClick={handleLinkClick}
                  >
                    Contact Us
                  </Link>
                </li>
              </ul>

              {/* App Download Section */}
              <div className="px-6 pb-6 mt-auto">
                <p className="text-[#090914] text-[14px] font-[600] mb-4 text-center">
                  Download App
                </p>
                <div className="flex flex-row gap-2 w-full">
                  <a
                    href={strings.playStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleLinkClick}
                    className="flex-1"
                  >
                    <div className="flex items-center justify-center border border-[#D9D9D9] rounded-lg p-2 gap-2 bg-black h-[40px]">
                      <Image
                        src={images.googlePlay.src}
                        alt={images.googlePlay.alt}
                        width={24}
                        height={24}
                        loading="lazy"
                        className="h-[1.5rem]"
                      />
                      <div className="flex flex-col">
                        <p className="text-[10px] text-white font-[400] mb-0 leading-tight">
                          Get it on
                        </p>
                        <p className="text-[14px] text-white font-[400] mb-0 leading-tight">
                          Google Play
                        </p>
                      </div>
                    </div>
                  </a>
                  <a
                    href={strings.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleLinkClick}
                    className="flex-1"
                  >
                    <div className="flex items-center justify-center border border-[#D9D9D9] rounded-lg p-2 gap-2 bg-black h-[40px]">
                      <img
                        src={images.appleStore.src}
                        alt={images.appleStore.alt}
                        className="h-[1.5rem] brightness-0 invert"
                      />
                      <div className="flex flex-col">
                        <p className="text-[10px] text-white font-[400] mb-0 leading-tight">
                          Download on the
                        </p>
                        <p className="text-[14px] text-white font-[400] mb-0 leading-tight">
                          App Store
                        </p>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </nav>
        </>
      )}
    </>
  );
}
